// Local notifications for overdue rentals — native Android only.
// Uses @capacitor/local-notifications which is unavailable in browser.
// Silently does nothing when running as web PWA.

import { isNativePlatform } from './platform'

interface RentalForNotification {
  id: string
  status: string
  plannedReturnAt: string
  bike: { code: string }
}

async function getNativeNotifications() {
  if (!isNativePlatform()) return null
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications')
    return LocalNotifications
  } catch {
    return null
  }
}

// Convert rental UUID to a stable numeric ID for notification scheduling.
// LocalNotifications requires a numeric ID.
function rentalIdToInt(uuid: string): number {
  // Take first 8 hex chars and convert to positive int
  return parseInt(uuid.replace(/-/g, '').substring(0, 7), 16) % 2147483647
}

export async function requestNotificationPermissions(): Promise<void> {
  const LocalNotifications = await getNativeNotifications()
  if (!LocalNotifications) return

  const { display } = await LocalNotifications.checkPermissions()
  if (display !== 'granted') {
    await LocalNotifications.requestPermissions()
  }
}

export async function scheduleOverdueNotifications(
  rentals: RentalForNotification[],
): Promise<void> {
  const LocalNotifications = await getNativeNotifications()
  if (!LocalNotifications) return

  // Cancel all previously scheduled overdue notifications
  const pending = await LocalNotifications.getPending()
  if (pending.notifications.length > 0) {
    await LocalNotifications.cancel({ notifications: pending.notifications })
  }

  const now = new Date()
  const toSchedule = rentals
    .filter((r) => r.status === 'active' && r.plannedReturnAt)
    .map((r) => ({
      id: rentalIdToInt(r.id),
      title: 'Просрочка аренды',
      body: `${(r as any).lines?.length > 0 ? (r as any).lines.map((l: any) => `#${l.bike.code}`).join(', ') : `#${r.bike?.code ?? '?'}`} — время истекло, нужно закрыть аренду`,
      schedule: { at: new Date(r.plannedReturnAt) },
      channelId: 'overdue',
      actionTypeId: '',
      extra: { rentalId: r.id },
    }))
    .filter((n) => n.schedule.at > now)

  if (toSchedule.length === 0) return

  // Ensure notification channel exists (Android 8+)
  await LocalNotifications.createChannel({
    id: 'overdue',
    name: 'Просроченные аренды',
    description: 'Уведомления об истёкшем времени аренды',
    importance: 4, // HIGH
    visibility: 1,
    vibration: true,
  }).catch(() => {}) // Channel may already exist

  await LocalNotifications.schedule({ notifications: toSchedule })
}
